import { createContext, useContext } from 'react'
import { cn } from '@/utils/cn'

/**
 * CSS-grid table shared across the case pages (documents, entity-resolution,
 * identity, …). One column template, defined once on <GridTable>, is shared by
 * the header and every row via context so they always line up.
 *
 *   <GridTable columns="28px minmax(0,1fr) 80px">
 *     <GridTableHeader>
 *       <div />
 *       <div>Name</div>
 *       <div>Status</div>
 *     </GridTableHeader>
 *     <div className="overflow-auto">
 *       {rows.map(r => (
 *         <GridTableRow key={r.id} active={r.id === openId} selected={r.checked} onClick={...}>
 *           …cells…
 *         </GridTableRow>
 *       ))}
 *     </div>
 *   </GridTable>
 *
 * Visual language is the Documents table: muted uppercase header, hairline row
 * dividers, hover highlight, and primary-tinted selected/active states.
 */
const GridTableColumnsContext = createContext<string | null>(null)

function useGridColumns(columns?: string): string {
  const ctx = useContext(GridTableColumnsContext)
  const resolved = columns ?? ctx
  if (resolved == null) {
    throw new Error(
      'GridTableHeader/GridTableRow must be inside <GridTable> or given a `columns` prop'
    )
  }
  return resolved
}

export interface GridTableProps {
  /** CSS `grid-template-columns` track list shared by the header and rows. */
  columns: string
  children: React.ReactNode
}

/** Provides the shared column template to descendant header/rows (renders no DOM). */
export function GridTable({ columns, children }: GridTableProps) {
  return (
    <GridTableColumnsContext.Provider value={columns}>{children}</GridTableColumnsContext.Provider>
  )
}

export interface GridTableHeaderProps extends React.ComponentProps<'div'> {
  /** Override the column template from context (rarely needed). */
  columns?: string
}

export function GridTableHeader({ columns, className, style, ...props }: GridTableHeaderProps) {
  const cols = useGridColumns(columns)
  return (
    <div
      className={cn(
        'grid shrink-0 items-center gap-2 border-b border-border bg-muted/30 px-4 py-2 text-2xs font-medium uppercase tracking-wider text-muted-foreground',
        className
      )}
      style={{ gridTemplateColumns: cols, ...style }}
      {...props}
    />
  )
}

export interface GridTableRowProps extends React.ComponentProps<'div'> {
  columns?: string
  /** Row is opened in a detail panel — primary tint + left accent bar. */
  active?: boolean
  /** Row is checkbox-selected (multi-select) — subtle primary tint. */
  selected?: boolean
  /** Hover highlight + pointer. Defaults to true when `onClick` is set. */
  interactive?: boolean
}

export function GridTableRow({
  columns,
  className,
  style,
  active,
  selected,
  interactive,
  onClick,
  ...props
}: GridTableRowProps) {
  const cols = useGridColumns(columns)
  const clickable = interactive ?? onClick != null
  return (
    <div
      className={cn(
        'group grid items-center gap-2 border-b border-border/50 px-4 py-2.5 text-sm transition-colors',
        clickable && 'cursor-pointer hover:bg-muted/50',
        active && 'border-l-2 border-l-primary bg-primary/10',
        selected && !active && 'bg-primary/5',
        className
      )}
      style={{ gridTemplateColumns: cols, ...style }}
      onClick={onClick}
      {...props}
    />
  )
}
